/**
 * Email parsing and cleaning utilities for displaying email content
 */

/**
 * Decodes quoted-printable encoded text commonly found in emails
 */
function decodeQuotedPrintable(input: string): string {
  if (!input) return "";

  return input
    .replace(/=3D/g, "=")
    .replace(/=E2=80=AF/g, " ") // Non-breaking space
    .replace(/=C2=A0/g, " ") // Non-breaking space
    .replace(/=20/g, " ") // Space
    .replace(/=0A/g, "\n") // Line feed
    .replace(/=0D/g, "\r") // Carriage return
    .replace(/=09/g, "\t") // Tab
    .replace(/=\r\n/g, "") // Soft line breaks
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/=([0-9A-F]{2})/gi, (_, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });
}

/**
 * Extracts only the newest message from an email thread,
 * removing all quoted replies and headers
 */
function extractNewestMessage(text: string): string {
  if (!text) return "";

  // Remove any error text if present
  text = text.replace(/^parsing failed for last email\s*/i, "");

  // Split into lines for processing
  const lines = text.split(/\r?\n/);
  const result: string[] = [];

  // Track if we've found the first "On ... wrote:" line that indicates the start of a quoted reply
  let foundFirstQuote = false;

  // Process lines to find content before the first quote
  for (const line of lines) {
    // Gmail-style header for quoted content typically starts with "On ... wrote:"
    if (/^On .* wrote:/.test(line.trim())) {
      foundFirstQuote = true;
      break; // Stop once we hit the first quoted reply
    }

    // Check for '>' character at the start which indicates a quoted line
    if (line.trim().startsWith(">")) {
      foundFirstQuote = true;
      break;
    }

    // Look for common reply separators
    if (line.trim().match(/^(-{2,}|\*{2,}|_{2,})/)) {
      foundFirstQuote = true;
      break;
    }

    // Look for "From:" headers that indicate forwarded/quoted content
    if (line.trim().match(/^From:/i) && result.length > 5) {
      foundFirstQuote = true;
      break;
    }

    // This is original content before any quoted replies
    result.push(line);
  }

  // If we couldn't find a clear separator, try heuristic approach
  if (!foundFirstQuote && result.length === lines.length) {
    // Look for a line with a '>' - this usually indicates the start of a quoted section
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim().startsWith(">")) {
        // Return only the content before this line
        return lines.slice(0, i).join("\n").trim();
      }
    }
  }

  // Return the collected lines
  return result.join("\n").trim();
}

/**
 * Strips HTML tags and converts HTML content to plain text
 */
function stripHtmlTags(html: string): string {
  if (!html) return "";
  
  return html
    // Convert common HTML entities
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Convert line breaks
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    // Remove all other HTML tags
    .replace(/<[^>]*>/g, "")
    // Clean up whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Removes base64 encoded attachments and MIME boundaries
 */
function removeMimeContent(text: string): string {
  if (!text) return "";

  // Remove MIME boundaries and content
  text = text.replace(/--[a-zA-Z0-9_-]+[\s\S]*?--[a-zA-Z0-9_-]+--/g, "");
  
  // Remove base64 encoded content (long strings of alphanumeric characters)
  text = text.replace(/[A-Za-z0-9+/]{50,}={0,2}/g, "");
  
  // Remove content-type headers
  text = text.replace(/Content-Type:.*$/gm, "");
  text = text.replace(/Content-Transfer-Encoding:.*$/gm, "");
  text = text.replace(/Content-Disposition:.*$/gm, "");
  
  return text;
}

/**
 * Main function to parse and clean email content for display
 */
export function parseEmailContent(rawContent: string): string {
  if (!rawContent) return "";

  try {
    // Step 1: Remove MIME content and attachments
    let cleanContent = removeMimeContent(rawContent);
    
    // Step 2: Decode quoted-printable encoding
    cleanContent = decodeQuotedPrintable(cleanContent);
    
    // Step 3: Strip HTML if present
    cleanContent = stripHtmlTags(cleanContent);
    
    // Step 4: Extract only the newest message (remove quoted replies)
    cleanContent = extractNewestMessage(cleanContent);
    
    // Step 5: Clean up extra whitespace and formatting
    cleanContent = cleanContent
      .replace(/\r\n/g, "\n") // Normalize line endings
      .replace(/\n{3,}/g, "\n\n") // Remove excessive line breaks
      .replace(/[ \t]+/g, " ") // Remove excessive spaces
      .trim();
    
    // If content is empty or too short, return original (might be a short message)
    if (!cleanContent || cleanContent.length < 10) {
      return rawContent.substring(0, 500) + (rawContent.length > 500 ? "..." : "");
    }
    
    return cleanContent;
    
  } catch (error) {
    console.error("Error parsing email content:", error);
    // Fallback: return first 500 characters of original content
    return rawContent.substring(0, 500) + (rawContent.length > 500 ? "..." : "");
  }
}

/**
 * Get a clean preview of email content (first few lines)
 */
export function getEmailPreview(rawContent: string, maxLength: number = 100): string {
  const cleanContent = parseEmailContent(rawContent);
  
  // Get first line or first sentence
  const firstLine = cleanContent.split('\n')[0] || cleanContent;
  
  if (firstLine.length <= maxLength) {
    return firstLine;
  }
  
  return firstLine.substring(0, maxLength).trim() + "...";
} 